import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, ArrowUpRight, CalendarClock, CircleAlert, CircleCheck, Copy, FileText, MessageSquareReply, Search, Square, UserRound, UsersRound,
} from 'lucide-react';
import { WsSegmented } from 'src/stage/WsControls';
import { Monogram, PageHeader, Skeleton, StagePage, Status, WsDialog, formatNumber, formatWhen } from 'src/stage/StageUi';
import broadcastApi, { currentConnection, readErrors } from './broadcastApi';
import { BROADCAST_STATUS, RECIPIENT_FILTERS, RECIPIENT_STATUS, audienceLabel, friendlyError, isOverdue, peopleCount, progress, rate } from './broadcastModel';
import MessagePreview from './MessagePreview';
import useConnectedNumber from 'src/stage/useConnectedNumber';
import './broadcasts.css';

function useLive(load, live, every = 4000) {
  useEffect(() => {
    if (!live) return undefined;
    const timer = setInterval(() => load(true), every);
    return () => clearInterval(timer);
  }, [live, load, every]);
}

function FunnelCard({ broadcast }) {
  const stats = broadcast.stats || {};
  const total = stats.total || broadcast.total_recipients || 0;
  const steps = [
    { key: 'sent', label: 'Sent', value: stats.sent, of: total, hint: `of ${peopleCount(total)}` },
    { key: 'delivered', label: 'Delivered', value: stats.delivered, of: stats.sent },
    { key: 'read', label: 'Read', value: stats.read, of: stats.sent },
    { key: 'replied', label: 'Replied', value: stats.replied, of: stats.sent, icon: MessageSquareReply },
  ];
  const done = progress(stats);
  return (
    <section className="ws-card wa-bc-funnel ws-rise" aria-label="Results">
      {broadcast.status === 'sending' && (
        <div className="wa-bc-progress">
          <span className="wa-live-dot" aria-hidden="true" />
          <strong>Sending</strong>
          <span className="ws-muted">{formatNumber(total - (stats.pending || 0))} of {formatNumber(total)} done</span>
          <span className="ws-meter ws-grow" role="meter" aria-label="Progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={done}><span style={{ width: `${done}%` }} /></span>
          <strong className="ws-num">{done}%</strong>
        </div>
      )}
      {broadcast.status === 'scheduled' && (
        <div className="wa-bc-progress"><CalendarClock size={16} color="#0F6392" />
          {isOverdue(broadcast)
            ? <><strong>Starting</strong><span className="ws-muted">It was due {formatWhen(broadcast.scheduled_at)}. Sending to {peopleCount(total)} starts within a minute; this page updates by itself.</span></>
            : <><strong>Scheduled</strong><span className="ws-muted">Sends {formatWhen(broadcast.scheduled_at)} to {peopleCount(total)}.</span></>}
        </div>
      )}
      <div className="wa-funnel">
        {steps.map((step) => {
          const pct = rate(step.value, step.of);
          const Icon = step.icon;
          return (
            <div key={step.key} className={`wa-funnel-step wa-funnel-step--${step.key}`}>
              <span className="ws-kpi-label">{Icon && <Icon size={14} aria-hidden="true" />}{step.label}</span>
              <span className="ws-kpi-value">{formatNumber(step.value || 0)}</span>
              <span className="wa-funnel-bar" aria-hidden="true"><span style={{ width: `${pct || 0}%` }} /></span>
              <span className="ws-muted" style={{ fontSize: 12 }}>{step.hint || (pct === null ? '—' : `${pct}% of sent`)}</span>
            </div>
          );
        })}
      </div>
      {(stats.failed > 0 || stats.skipped > 0 || stats.uncertain > 0 || stats.cancelled > 0) && (
        <div className="wa-bc-issues">
          {stats.failed > 0 && <span className="ws-status ws-status--failed">{formatNumber(stats.failed)} failed</span>}
          {stats.uncertain > 0 && <span className="ws-status ws-status--warning">{formatNumber(stats.uncertain)} to check</span>}
          {stats.skipped > 0 && <span className="ws-status ws-status--draft">{formatNumber(stats.skipped)} skipped</span>}
          {stats.cancelled > 0 && <span className="ws-status ws-status--draft">{formatNumber(stats.cancelled)} not sent (stopped)</span>}
        </div>
      )}
    </section>
  );
}

function Recipients({ phoneNumberId, broadcast, connection, live }) {
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [state, setState] = useState({ loading: true, rows: [], pagination: null });
  const sequence = useRef(0);
  useEffect(() => { const timer = setTimeout(() => setSearch(query.trim()), 300); return () => clearTimeout(timer); }, [query]);
  useEffect(() => { setPageNumber(1); }, [filter, search]);
  const load = useCallback(async (quiet = false) => {
    const version = ++sequence.current;
    if (!quiet) setState((current) => ({ ...current, loading: true }));
    try {
      const data = await broadcastApi.recipients(phoneNumberId, broadcast.id, { status: filter, search: search || undefined, page: pageNumber, per_page: 25 });
      if (version === sequence.current) setState({ loading: false, rows: data.data || [], pagination: data.pagination });
    } catch {
      if (version === sequence.current) setState((current) => ({ ...current, loading: false, error: true }));
    }
  }, [phoneNumberId, broadcast.id, filter, search, pageNumber]);
  useEffect(() => { load(); }, [load]);
  useLive(load, live, 5000);
  const stats = broadcast.stats || {};
  const counts = { all: stats.total, read: stats.read, replied: stats.replied, delivered: stats.delivered, pending: stats.pending, failed: (stats.failed || 0) + (stats.uncertain || 0) };
  const items = RECIPIENT_FILTERS.filter((item) => item.id !== 'pending' || stats.pending > 0).map((item) => ({ ...item, count: counts[item.id] ?? undefined }));
  const chatLink = (row) => `/conversation?${new URLSearchParams({ conversation: row.conversation_id, workspace: connection.workspaceId, number: phoneNumberId })}`;
  const pages = state.pagination;

  return (
    <section className="ws-card wa-bc-people" aria-label="People">
      <div className="wa-card-head">
        <div className="ws-grow"><h2 className="ws-h3">People</h2><p className="ws-muted">Each person’s result updates as WhatsApp reports it.</p></div>
      </div>
      <div className="ws-controls wa-bc-controls">
        <WsSegmented label="Filter people" items={items} value={filter} onChange={setFilter} />
        <div className="ws-controls-end">
          <label className="ws-searchbox"><Search size={15} aria-hidden="true" /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search people" aria-label="Search people" /></label>
        </div>
      </div>
      <ul className="wa-list">
        {state.loading && !state.rows.length && [0, 1, 2, 3].map((i) => <li key={i} style={{ padding: '10px 12px' }}><Skeleton height={34} /></li>)}
        {!state.loading && !state.rows.length && <li className="ws-empty" style={{ margin: '4px 8px 8px' }}><strong>No one here</strong>{filter === 'replied' ? 'Replies show up here as people answer.' : 'Try another filter.'}</li>}
        {state.rows.map((row) => {
          const meta = RECIPIENT_STATUS[row.status] || RECIPIENT_STATUS.pending;
          return (
            <li key={row.id} className="wa-list-item">
              <div className="wa-person">
                <Monogram name={row.name || '#'} size={32} />
                <span className="wa-person-name">
                  <strong className="wa-ellipsis">{row.name || 'No name saved'}</strong>
                  <span className="ws-muted">{row.phone_number}</span>
                </span>
                <span className="wa-person-state">
                  <Status tone={meta.tone}>{meta.label}</Status>
                  {row.replied && <span className="wa-replied"><MessageSquareReply size={12} aria-hidden="true" />Replied</span>}
                </span>
                <span className="ws-muted wa-person-time">{row.sent_at ? formatWhen(row.sent_at) : ''}</span>
                <span className="wa-person-go">
                  {row.conversation_id && <Link className="ws-agent-open" to={chatLink(row)}>Chat<ArrowUpRight size={13} aria-hidden="true" /></Link>}
                </span>
                {row.error && <p className="wa-person-error" title={row.error}><CircleAlert size={13} aria-hidden="true" />{friendlyError(row.error)}</p>}
              </div>
            </li>
          );
        })}
      </ul>
      {pages && pages.last_page > 1 && (
        <div className="wa-pager">
          <span>Page {pages.current_page} of {pages.last_page} · {peopleCount(pages.total)}</span>
          <span className="wa-pager-buttons">
            <button type="button" className="ws-btn ws-btn--quiet ws-btn--sm" disabled={pages.current_page <= 1} onClick={() => setPageNumber((n) => n - 1)}>Previous</button>
            <button type="button" className="ws-btn ws-btn--quiet ws-btn--sm" disabled={pages.current_page >= pages.last_page} onClick={() => setPageNumber((n) => n + 1)}>Next</button>
          </span>
        </div>
      )}
    </section>
  );
}

export default function BroadcastDetail() {
  const { broadcastId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const connection = currentConnection();
  const phoneNumberId = connection?.phoneNumberId;
  const [state, setState] = useState({ loading: true, broadcast: null, error: null });
  const [confirmStop, setConfirmStop] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [notice, setNotice] = useState(location.state?.created ? 'created' : '');
  const number = useConnectedNumber(phoneNumberId);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setState((current) => ({ ...current, loading: true }));
    try {
      const broadcast = await broadcastApi.show(phoneNumberId, broadcastId);
      setState({ loading: false, broadcast, error: null });
    } catch (error) {
      setState((current) => ({ ...current, loading: false, error: readErrors(error), notFound: error?.response?.status === 404 }));
    }
  }, [phoneNumberId, broadcastId]);
  useEffect(() => { load(); }, [load]);
  const broadcast = state.broadcast;
  const live = broadcast && (broadcast.status === 'sending' || (broadcast.status === 'scheduled' && new Date(broadcast.scheduled_at) <= new Date(Date.now() + 60000)));
  useLive(load, live);

  const stop = async () => {
    setStopping(true);
    try {
      const updated = await broadcastApi.cancel(phoneNumberId, broadcastId);
      setState((current) => ({ ...current, broadcast: updated }));
      setNotice('stopped');
    } catch (error) {
      setState((current) => ({ ...current, error: readErrors(error) }));
    } finally {
      setStopping(false);
      setConfirmStop(false);
    }
  };

  if (state.loading && !broadcast) {
    return <StagePage className="wa-bc" label="Broadcast"><Skeleton width={160} height={14} /><Skeleton width={340} height={34} /><Skeleton height={160} radius={20} /><Skeleton height={320} radius={20} /></StagePage>;
  }
  if (!broadcast) {
    return (
      <StagePage className="wa-bc" label="Broadcast">
        <Link to="/broadcast" className="wa-back"><ArrowLeft size={15} aria-hidden="true" />All broadcasts</Link>
        <div className="ws-empty"><strong>{state.notFound ? 'This broadcast isn’t here' : 'Couldn’t open this broadcast'}</strong>{state.error?.summary}</div>
      </StagePage>
    );
  }

  const meta = BROADCAST_STATUS[broadcast.status] || BROADCAST_STATUS.completed;
  const open = broadcast.status === 'scheduled' || broadcast.status === 'sending';
  const timeline = [
    ['Created', broadcast.created_at, broadcast.created_by?.name ? `by ${broadcast.created_by.name}` : ''],
    [broadcast.status === 'scheduled' ? 'Sends' : 'Scheduled for', broadcast.scheduled_at, ''],
    ['Started', broadcast.started_at, ''],
    ['Finished', broadcast.completed_at, ''],
    ['Stopped', broadcast.cancelled_at, ''],
  ].filter(([, value]) => value);

  return (
    <StagePage className="wa-bc" label={broadcast.name}>
      <Link to="/broadcast" className="wa-back"><ArrowLeft size={15} aria-hidden="true" />All broadcasts</Link>
      <PageHeader
        eyebrow="Broadcast"
        title={broadcast.name}
        actions={(
          <>
            <button type="button" className="ws-btn ws-btn--quiet" onClick={() => navigate('/broadcast/new', { state: { from: broadcast } })}><Copy size={15} />Send again</button>
            {open && <button type="button" className="ws-btn ws-btn--quiet wa-btn-stop" onClick={() => setConfirmStop(true)}><Square size={13} />{broadcast.status === 'scheduled' ? 'Cancel' : 'Stop sending'}</button>}
          </>
        )}
      >
        <div className="wa-bc-headline">
          <Status tone={meta.tone}>{meta.label}</Status>
          <span className="ws-muted"><FileText size={13} aria-hidden="true" />{broadcast.template?.name}</span>
          <span className="ws-muted"><UsersRound size={13} aria-hidden="true" />{audienceLabel(broadcast.audience)}</span>
        </div>
      </PageHeader>

      {notice === 'created' && (
        <div className="wa-notice wa-notice--good" role="status"><CircleCheck size={16} /><span className="ws-grow">{broadcast.status === 'scheduled' ? `Scheduled for ${formatWhen(broadcast.scheduled_at)}.` : 'Sending has started. You can leave this page; it keeps going.'}</span><button type="button" className="ws-btn ws-btn--plain" onClick={() => setNotice('')}>Dismiss</button></div>
      )}
      {notice === 'stopped' && <div className="wa-notice wa-notice--info" role="status"><Square size={14} /><span className="ws-grow">Stopped. People who already got it keep their message; nobody else will.</span></div>}
      {state.error && state.broadcast && <div className="wa-notice wa-notice--bad" role="alert"><CircleAlert size={16} /><span>{state.error.summary}</span></div>}

      <FunnelCard broadcast={broadcast} />

      <div className="wa-bc-detail-grid">
        <Recipients phoneNumberId={phoneNumberId} broadcast={broadcast} connection={connection} live={live} />
        <aside className="wa-bc-detail-side" aria-label="Message and details">
          <MessagePreview template={broadcast.template} variables={broadcast.variables || {}} sample={{ name: 'Asha Rao', business: number?.verified_name || number?.display_name || 'Your business' }} caption="Example" />
          <section className="ws-card ws-card-pad wa-bc-facts">
            <h2 className="ws-h3">Details</h2>
            <dl>
              <div><dt>Template</dt><dd>{broadcast.template?.name} · {broadcast.template?.language}</dd></div>
              <div><dt>Audience</dt><dd>{audienceLabel(broadcast.audience)} · {peopleCount(broadcast.total_recipients)}</dd></div>
              {timeline.map(([label, value, extra]) => <div key={label}><dt>{label}</dt><dd>{formatWhen(value)} {extra && <span className="ws-muted"><UserRound size={12} aria-hidden="true" /> {extra}</span>}</dd></div>)}
            </dl>
          </section>
        </aside>
      </div>

      {confirmStop && (
        <WsDialog title={broadcast.status === 'scheduled' ? 'Cancel this broadcast?' : 'Stop sending?'} description={broadcast.status === 'scheduled' ? 'Nobody will get it. You can send it again later.' : 'People who already got it keep their message. Nobody else will get it.'} onClose={() => !stopping && setConfirmStop(false)}>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="ws-btn ws-btn--quiet" data-close onClick={() => setConfirmStop(false)} disabled={stopping}>Keep it</button>
            <button type="button" className="ws-btn ws-btn--danger" onClick={stop} disabled={stopping}><Square size={13} />{stopping ? 'Stopping…' : broadcast.status === 'scheduled' ? 'Cancel broadcast' : 'Stop sending'}</button>
          </div>
        </WsDialog>
      )}
    </StagePage>
  );
}
